import React, { useMemo, useState } from "react";
import {
    BadgeCounter,
    Button,
    ListItem,
    SortMenu,
} from "../../../components";
import InputRadio from "../../../components/input-radio";
import { getDocExtension, getMetadataItems, isAnnexDoc } from "../utils";

// Shared scaffolding for the in-page docs wizards (Affiliate filters, Manage annexes):
// a left column listing the selected documents and a right action panel.

export const WIZARD_SORT_OPTIONS = [
    { id: "newest", label: "Newest first" },
    { id: "oldest", label: "Older first", withHr: true },
    { id: "az", label: "A - Z" },
    { id: "za", label: "Z - A" },
];

// Client-side sort of the (already selected) docs shown on the left.
export const sortDocs = (docs, sortValue) => {
    const sorted = [...docs];
    switch (sortValue) {
        case "az":
            return sorted.sort((a, b) => (a.displayName || "").localeCompare(b.displayName || ""));
        case "za":
            return sorted.sort((a, b) => (b.displayName || "").localeCompare(a.displayName || ""));
        case "oldest":
            return sorted.reverse();
        case "newest":
        default:
            return sorted;
    }
};

// Left column: optional info note + count badge + sort + the document rows.
// `count` overrides the badge number (defaults to the number of rows shown).
export const WizardDocsPanel = ({ docs = [], note, count, getRowDisabled }) => {
    const [sortValue, setSortValue] = useState("newest");
    const sortedDocs = useMemo(() => sortDocs(docs, sortValue), [docs, sortValue]);

    return (
        <section className="docs-wizard-docs">
            <div className="docs-wizard-docs-header">
                <div className="docs-wizard-note">
                    {note && (
                        <>
                            <img src="/images/icon/info.svg" alt="info" className="docs-wizard-note-icon" />
                            <span>{note}</span>
                        </>
                    )}
                    <BadgeCounter count={count ?? docs.length} imgSrc="/images/icon/spec.svg" />
                </div>
                <SortMenu options={WIZARD_SORT_OPTIONS} value={sortValue} onChange={setSortValue} />
            </div>

            <div className="docs-wizard-docs-list">
                {sortedDocs.map((doc) => (
                    <WizardDocRow key={doc.id} doc={doc} disabled={getRowDisabled && getRowDisabled(doc)} />
                ))}
            </div>
        </section>
    );
};

const WizardDocRow = ({ doc, disabled }) => {
    const ext = getDocExtension(doc);
    const items = useMemo(() => getMetadataItems(doc), [doc]);
    const isAnnex = isAnnexDoc(doc);

    return (
        <div className={`docs-wizard-doc-row ${disabled ? "disabled" : ""}`}>
            {isAnnex && (
                <img src="/images/icon/list-item-child.svg" alt="annex" className="docs-wizard-annex-icon" />
            )}
            <ListItem
                title={
                    <div className="doc-title">
                        <span>{doc?.documentNo}</span>
                        <span className="doc-title-text">{doc?.displayName}</span>
                    </div>
                }
                titleTooltip={doc?.displayName}
                type="secondary"
                items={items}
                renderRightSection={ext ? <span className="doc-file-ext">{ext}</span> : null}
            />
        </div>
    );
};

// Right panel shell: title header, scrollable body, footer button.
export const WizardRightPanel = ({ title, iconSrc, headerAction, children, canApply, applying, onApply, applyLabel = "APPLY", applyIconSrc }) => (
    <aside className="docs-wizard-panel">
        <div className="docs-wizard-panel-header">
            {iconSrc && <img src={iconSrc} alt="" className="docs-wizard-panel-icon" />}
            <h2 className="docs-wizard-panel-title truncate-text" title={title}>{title}</h2>
            {headerAction && <div className="docs-wizard-panel-header-action">{headerAction}</div>}
        </div>

        <div className="docs-wizard-panel-body">{children}</div>

        <div className="docs-wizard-footer">
            <Button
                type="secondary"
                theme="red"
                disable={!canApply}
                loading={applying}
                className="docs-wizard-apply"
                onClick={onApply}
            >
                {applyIconSrc && <img src={applyIconSrc} alt="" className="docs-wizard-apply-icon" />}
                <span>{applyLabel}</span>
            </Button>
        </div>
    </aside>
);

// Step one of every docs wizard: pick what to do, then CONTINUE.
export const WizardActionChoice = ({
    label = "What do you want to do?",
    options = [],
    value,
    onChange,
}) => (
    <div className="docs-wizard-action-block">
        <label className="docs-wizard-action-label">{label}</label>
        <div className="docs-wizard-action-options">
            {options.map((option) => (
                <InputRadio
                    key={option.id}
                    id={`docs-wizard-action-${option.id}`}
                    name="docs-wizard-action"
                    value={option.id}
                    checked={value === option.id}
                    label={<><strong>{option.lead}</strong> {option.rest}</>}
                    onChange={() => onChange && onChange(option.id)}
                />
            ))}
        </div>
    </div>
);
